import { useEffect, useState, useRef } from "react";
import { useForm } from "../../../hooks";
import {
  useSelectOptionsStore,
  useFormEventStore,
  useFormMessages,
  useScheduleEventStore,
} from "../../hooks";
import {
  LoadingData,
  Select,
  TextArea,
  Input,
  ErrorMessage,
  MainButton,
  SecondaryButton,
} from "../../ui";

const initialFormData = {
  protocolId: "",
};

const initialFormValidations = {
  protocolId: [
    (val) => (val ?? "").toString().trim().length > 0,
    "Selecciona un protocolo",
  ],
};

const allowedAvalProcedureIds = [
  "78763555-39b4-4433-a7f2-079dea69b64a",
  "3d44fc12-6947-42c8-aaec-49b2fca42681",
  "2c7d0781-fe65-4f42-b868-bdc89815c849",
  "2f277fe7-ed78-4f6a-9f38-be07690e2c79",
  "4f5340ea-13f2-45ed-8906-e7b4574653af",
  "697b2caa-45e0-475a-9c3e-46b85faabf9b",
  "e4c80dfc-7f37-42cb-94fb-4ad0e475c742",
  "49c7832b-6a29-456e-8acc-fe00137a6455",
];
const AUTO_FILL_PROTOCOL_ID = "6ba57617-f227-11f0-bea3-066d180a3717";

export const PatientSurgeryAssessmentForm = ({ patientSurgery }) => {
  // Datos del hook ----------------------------------------//
  const {
    // Props
    protocols,

    // Methods
    getProtocols,
  } = useSelectOptionsStore();

  const {
    // Props
    isLoading,
    message,
  } = useFormEventStore();

  const {
    // Methods
    startPatientSurgeryAssessment,
    startPatientSurgeryAssessmentNoShow,
  } = useScheduleEventStore();

  const { procedure } = patientSurgery;

  // States ------------------------------------------------//
  const [validateForm, setValidateForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formValidations, setFormValidations] = useState(
    initialFormValidations,
  );

  // Formulario --------------------------------------------//
  const { formState, formValidation, onInputChange, isFormValid } = useForm(
    formData,
    formValidations,
  );

  const {
    setFormSubmitted,
    showErrorMessage,
    showSuccessMessage,
    messageText,
  } = useFormMessages({
    message,
    setValidateForm,
  });

  // Funciones ---------------------------------------------//
  const onSubmit = (event) => {
    event.preventDefault();
    setValidateForm(true);
    if (isFormValid) {
      setFormSubmitted(true);
      startPatientSurgeryAssessment(formState);
    }
  };

  const onSubmitAssessmentNoShow = () => {
    setFormSubmitted(true);
    startPatientSurgeryAssessmentNoShow();
  };

  const handleProtocolChange = (event) => {
    const { name, value } = event.target;

    const protocol = protocols.data.find(
      (protocol) => protocol.id === value,
    ).text;

    // console.log("protocol", protocol);

    setFormData(() => {
      const updated = {
        ...formState,
        [name]: value,
        protocol,
      };

      return updated;
    });

    setFormValidations(() => {
      const updated = {
        ...formValidations,
        protocol: [
          (val) => (val ?? "").toString().trim().length > 0,
          "Escribe un protocolo",
        ],
      };

      return updated;
    });
  };

  const clearanceStatusChange = (event) => {
    const { name, value } = event.target;

    setFormData(() => {
      const updated = {
        ...formState,
        [name]: value,
      };

      if (value === "NO_BUT_CAN_RESCHEDULE" || value === "NO") {
        updated.clearanceNote = "";
        updated.numberEvents = 0;
      } else {
        delete updated.clearanceNote;
        updated.numberEvents = 1;
      }

      return updated;
    });

    setFormValidations(() => {
      const updated = {
        ...formValidations,
      };

      if (value === "NO_BUT_CAN_RESCHEDULE" || value === "NO") {
        updated.clearanceNote = [
          (val) => (val ?? "").toString().trim().length > 0,
          "Escribe el motivo",
        ];
      } else {
        delete updated.clearanceNote;
      }

      return updated;
    });
  };

  // Efectos -----------------------------------------------//
  useEffect(() => {
    if (!patientSurgery?.id) return;

    getProtocols({
      protocolId: AUTO_FILL_PROTOCOL_ID,
      id: patientSurgery.id,
    });
  }, [patientSurgery.id, getProtocols]);

  useEffect(() => {
    if (procedure) {
      if (!allowedAvalProcedureIds.includes(procedure.id)) return;

      setFormData(() => {
        const updated = {
          ...formState,
          clearanceStatus: "",
          numberEvents: 1,
        };

        return updated;
      });

      setFormValidations(() => {
        const updated = {
          ...formValidations,
          clearanceStatus: [
            (val) => (val ?? "").toString().trim().length > 0,
            "Selecciona una opción",
          ],
        };

        delete updated.numberEvents;

        return updated;
      });
    }
  }, [procedure]);

  // useEffect(() => {
  //   console.log(formState);
  // }, [formState]);

  //   useEffect(() => {
  //     console.log(formValidation);
  //   }, [formValidation]);

  return (
    <form onSubmit={onSubmit}>
      <div className="row gy-3 mx-auto" style={{ maxWidth: "500px" }}>
        {/* Formulario estatico */}
        <>
          <div className="col-12">
            <Select
              label={"Selecciona el protocolo"}
              options={protocols.data}
              labelKey={"name"}
              validation={formValidation.protocolIdValid}
              submit={validateForm}
              name={"protocolId"}
              value={formState.protocolId}
              onInputChange={handleProtocolChange}
              disabled={protocols.isLoading}
            />
          </div>

          {formState.protocolId && (
            <div className="col-12">
              <TextArea
                label={"Protocolo"}
                placeholder={"Protocolo"}
                validation={formValidation.protocolValid}
                submit={validateForm}
                name={"protocol"}
                value={formState.protocol}
                onInputChange={onInputChange}
                disabled={protocols.isLoading}
              />
            </div>
          )}

          {allowedAvalProcedureIds.includes(procedure.id) ? (
            <div className="col-md-12">
              <label className="form-label">
                {"¿Está autorizado para procedimiento?"}
              </label>
              <div className="custom-control custom-radio custom-control-inline">
                <div className="form-check mb-0">
                  <input
                    id="clearanceStatus-YES"
                    className="form-check-input me-2"
                    readOnly
                    type="radio"
                    name={`clearanceStatus`}
                    value={"YES"}
                    checked={formState.clearanceStatus === "YES"}
                    onChange={clearanceStatusChange}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="clearanceStatus-YES"
                  >
                    Sí
                  </label>
                </div>
                <div className="form-check mb-0">
                  <input
                    id="clearanceStatus-NO_BUT_CAN_RESCHEDULE"
                    className="form-check-input me-2"
                    readOnly
                    type="radio"
                    name={`clearanceStatus`}
                    value={"NO_BUT_CAN_RESCHEDULE"}
                    checked={
                      formState.clearanceStatus === "NO_BUT_CAN_RESCHEDULE"
                    }
                    onChange={clearanceStatusChange}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="clearanceStatus-NO_BUT_CAN_RESCHEDULE"
                  >
                    No, pero puede reprogramar
                  </label>
                </div>
                {formState.clearanceStatus === "NO_BUT_CAN_RESCHEDULE" && (
                  <div className="mb-2">
                    <TextArea
                      placeholder={
                        "Describe el motivo por el cual el paciente puede reprogramar"
                      }
                      validation={formValidation.clearanceNoteValid}
                      submit={validateForm}
                      name={"clearanceNote"}
                      value={formState.clearanceNote}
                      onInputChange={onInputChange}
                      rows={2}
                    />
                  </div>
                )}
                <div className="form-check mb-0">
                  <input
                    id="clearanceStatus-NO"
                    className="form-check-input me-2"
                    readOnly
                    type="radio"
                    name={`clearanceStatus`}
                    value={"NO"}
                    checked={formState.clearanceStatus === "NO"}
                    onChange={clearanceStatusChange}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="clearanceStatus-NO"
                  >
                    No
                  </label>
                </div>
                {formState.clearanceStatus === "NO" && (
                  <div className="mb-2">
                    <TextArea
                      placeholder={
                        "Describe el motivo por el cual el paciente no está autorizado para el procedimiento"
                      }
                      validation={formValidation.clearanceNoteValid}
                      submit={validateForm}
                      name={"clearanceNote"}
                      value={formState.clearanceNote}
                      onInputChange={onInputChange}
                      rows={2}
                    />
                  </div>
                )}
              </div>
              {formValidation.clearanceStatusValid && validateForm && (
                <span className="text-danger">
                  {formValidation.clearanceStatusValid}
                </span>
              )}
            </div>
          ) : (
            <div className="col-md-12">
              <Input
                label={"Número de eventos"}
                type={"number"}
                placeholder={"Número de eventos"}
                validation={formValidation.numberEventsValid}
                submit={validateForm}
                name={"numberEvents"}
                value={formState.numberEvents}
                onInputChange={onInputChange}
              />
            </div>
          )}
        </>

        {(protocols.isLoading || isLoading) && (
          <div className="col-12">
            <LoadingData />
          </div>
        )}

        {showErrorMessage && (
          <div className="col-md-12">
            <ErrorMessage>{messageText}</ErrorMessage>
          </div>
        )}

        <div className="col-md-12 d-flex justify-content-end">
          <SecondaryButton
            disabled={isLoading || protocols.isLoading}
            type={"button"}
            onClick={() => onSubmitAssessmentNoShow()}
          >
            No asistió
          </SecondaryButton>
          <MainButton
            className={"ms-2"}
            type={"submit"}
            disabled={isLoading || protocols.isLoading}
          >
            Valorar
          </MainButton>
        </div>
      </div>
    </form>
  );
};
